import { useEffect, useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';
import { devLog } from '../../../../utils/log';
import { emailValidator } from '../../../../utils/validators';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { useSignUpWizardContext } from '../../wizards/signUpWizard.provider';
import { useHandleSignUpWizardRedesign } from '../../wizards/useHandleSignUpWizardRedesign';
import { HeaderLogoElement } from '../HeaderLeftElement/HeaderLogoElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';
import { RedesignEmailAndPasswordInputs } from '../RedesignEmailAndPasswordInputs/RedesignEmailAndPasswordInputs';
import { ThirdPartyAuthRedesign } from '../ThirdPartyAuthRedesign/ThirdPartyAuthRedesign';

import styles from './SignUpOptionsRedesign.module.scss';
import { TermsAndServices } from './TermsAndServices';

export const SignUpOptionsRedesign = observer(function SignUpOptionsRedesign() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldsErrors, setFieldsErrors] = useState({ password: '', email: '' });
  const [bypassBlockSignupWithEmail, setBypassBlockSignupWithEmail] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [signupSessionId] = useState<string>(() => {
    const stored = localStorage.getItem('signup_session_id');
    if (stored) {
      localStorage.removeItem('signup_session_id');
      return stored;
    }
    return crypto.randomUUID();
  });
  const { t } = useTranslation();

  const { authStore, portalStore } = useStores();
  const { isSigningUp } = authStore;

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const blockSignupWithEmailFeatureFlag = useFlag('block_signup_with_email', false);

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { handleSignUpWizard } = useHandleSignUpWizardRedesign();
  const { error, updateContextData } = useSignUpWizardContext();

  const clearError = () => updateContextData({ error: '' });

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.data === 'bypassBlockSignupWithEmail') {
        setBypassBlockSignupWithEmail(true);
      }
    };

    window.addEventListener('message', eventHandler);
    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, []);

  useEffect(() => {
    const params: AnalyticsEvents['create_account'] = {
      event_action: 'impression',
      event_category: 'sign_up',
    };
    void analytics.sendEvent('create_account', params);

    devLog('create_account impression', { signup_session_id: signupSessionId });
  }, [signupSessionId]);

  const handleLogin = () => {
    const params: AnalyticsEvents['login_account_button'] = {
      event_action: 'click',
      event_category: 'sign_up',
      button_text: 'log in',
    };
    void analytics.sendEvent('login_account_button', params);
    openAuthWizard('logInWizard', {
      triggeredBy: 'sign_up_platform',
      force: true,
      persistent: false,
      landingKey: landingKey ?? undefined,
    });
  };

  const blockSignupWithEmail = blockSignupWithEmailFeatureFlag && !bypassBlockSignupWithEmail;

  const onTermsChange = (value: boolean) => {
    setIsTermsAccepted(value);
    if (value) {
      setApproveError('');
      void analytics.sendEvent('age_approve', {
        event_action: 'click',
        event_category: 'sign_up',
      });
    }
  };

  const validateApprovalFields = () => {
    if (!isTermsAccepted) {
      setApproveError(t('auth.approvalConditionsRequired'));
    } else {
      setApproveError('');
    }

    return isTermsAccepted;
  };

  const handleEmailSignUp = () => {
    const isValid = validateApprovalFields();

    if (!isValid) {
      return;
    }

    const emailError = email.trim() ? emailValidator(email.trim()) || '' : t('auth.pleaseFillInAllRequiredFields');
    const passwordError = password.trim() ? '' : t('auth.pleaseFillInAllRequiredFields');

    if (emailError || passwordError) {
      setFieldsErrors({ email: emailError, password: passwordError });
      updateContextData({ error: emailError || passwordError });
      return;
    }

    updateContextData({ error: '' });

    devLog('create_account_by_email clicked', { signup_session_id: signupSessionId });

    handleSignUpWizard({ provider: 'email', credentials: { email, password }, action: 'sign_up' });
  };

  const onClose = () => {
    void analytics.sendEvent('create_account', {
      event_action: 'click',
      event_category: 'sign_up',
      click_location: 'close',
    });
    portalStore.closePortal();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isSigningUp || blockSignupWithEmail) {
      return;
    }

    const targetId = (event.target as HTMLElement)?.id;

    if (event.key === 'Enter' && (targetId === 'email' || targetId === 'password')) {
      event.preventDefault();
      handleEmailSignUp();
    }
  };

  return (
    <RedesignContainer className={styles.container} onKeyDown={handleKeyDown}>
      <DialogHeader
        className={styles.dialogHeader}
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="signup-back-button"
        headerLeftElement={<HeaderLogoElement position="left" showLogo={isDesktop} />}
        headerRightElement={<HeaderLogoElement position="right" showLogo={!isDesktop} />}
        title={t('auth.createAccount')}
        titleClassName={styles.dialogHeaderTitle}
        {...(isDesktop ? { onClose } : { onGoBack: onClose })}
      />
      <div className={styles.containerContent}>
        <div className={styles.thirdPartyAuthWrapper}>
          <div className={styles.signUpOptionsSubtitleWrapper}>
            <span className={styles.signUpOptionsSubtitle}>{t('auth.signUpWith')}</span>
          </div>
          <ThirdPartyAuthRedesign
            action="Sign Up"
            signupSessionId={signupSessionId}
            onLoginAttempt={validateApprovalFields}
          />
        </div>

        <div className={styles.rowLineContainer}>
          <div className={classNames(styles.lineGradient, styles.lineGradientLeft)} />
          <span className={styles.orEmailText}>{t('auth.orEmail')}</span>
          <div className={classNames(styles.lineGradient, styles.lineGradientRight)} />
        </div>

        <RedesignEmailAndPasswordInputs
          className={styles.emailAndPasswordInputsWrapper}
          disabled={isSigningUp}
          emailError={fieldsErrors.email}
          hideErrors={true}
          passwordError={fieldsErrors.password}
          type="sign_up"
          onChangeEmail={(newEmail) => {
            clearError();
            setEmail(newEmail);
          }}
          onChangePassword={(newPassword) => {
            clearError();
            setPassword(newPassword);
          }}
        />

        <TermsAndServices
          isError={!!approveError}
          isTermsAccepted={isTermsAccepted}
          setIsTermsAccepted={onTermsChange}
        />

        <ErrorMessage className={styles.authErrorMessage}>
          {error || approveError || fieldsErrors.email || fieldsErrors.password}
        </ErrorMessage>

        {!blockSignupWithEmail && (
          <div className={styles.createAccountEmailButtonWrapper}>
            <Button
              btnType="main"
              className={styles.emailSignUpButton}
              dataTestId="signup-create-account-btn"
              onClick={handleEmailSignUp}
            >
              {t('auth.createAccount')}
            </Button>
          </div>
        )}

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
          data-testid="signup-redesign-already-have-account"
        >
          {/*
           * The title-casing stays in JS and arrives as a value. Baking the mutated result into the
           * key would change what renders, which is a separate change from this one.
           */}
          <Trans
            components={{
              login: <span className={styles.loginLink} data-testid="lobby-login-btn" onClick={handleLogin} />,
              question: <span />,
            }}
            i18nKey="auth.alreadyHaveAccountSentence"
            values={{ logIn: t('auth.logIn').charAt(0).toUpperCase() + t('auth.logIn').slice(1).toLowerCase() }}
          />
        </div>
      </div>
    </RedesignContainer>
  );
});
